// ask.ts — client for the Worker's /ask streaming endpoint (Phase 3 RAG).
//
// The Worker emits a custom SSE stream:
//   event: citations
//   data: { citations: [...] }
//
//   event: message_start / content_block_start / content_block_delta / ...
//   data: <Anthropic Messages API SSE payload>
//
//   event: message_stop / error
//
// We parse those events and surface them via small callbacks. The caller
// (AskPage) accumulates the streamed text and renders citations in a side
// panel as soon as the citations event arrives.

import { NOTES_ENDPOINT } from "../config/endpoints";

export type CitationSource = "document" | "commitment";

export interface Citation {
  /** Which corpus this hit came from. `commitment` hits get their own
   * styling in the UI so it's obvious the answer is grounded in a
   * specific person's accountability, not a doc. */
  source: CitationSource;
  docId: string;
  fileName: string;
  sectionLabel: string;
  chunkIdx: number;
  kind: string;
  score: number;
  snippet: string;
}

export interface AskTurn {
  role: "user" | "assistant";
  content: string;
}

/** Pipeline-stage update from the Worker. Each step of the /ask backend
 * (embedding → vectorize → image fetch → Claude call) emits one of these so
 * the UI can show a live "Searching… / Reading 1 image… / Drafting…" status
 * line instead of a static "Thinking…" placeholder. Fluency win without
 * changing token rate — the user watches the system work. */
export type AskStage =
  | { phase: "embedding" }
  | { phase: "retrieving" }
  | { phase: "retrieved"; docHits: number; docCount: number; commitmentHits: number }
  | { phase: "loading_images"; count: number }
  | { phase: "drafting" };

export interface AskCallbacks {
  /** Fired each time the Worker moves to the next pipeline stage. */
  onStage?: (stage: AskStage) => void;
  /** Fired once when citations arrive (before any answer tokens). */
  onCitations?: (citations: Citation[]) => void;
  /** Fired for each text chunk Claude emits. */
  onDelta?: (deltaText: string) => void;
  /** Fired once when the model has stopped (clean end). */
  onDone?: () => void;
  /** Fired on Worker error or upstream Anthropic error. */
  onError?: (message: string) => void;
}

/**
 * Send a question + optional history + a pre-computed live-data snapshot to
 * the Worker. Returns an AbortController so the caller can cancel mid-stream
 * (e.g. if the user navigates away or hits Stop).
 *
 * `liveData` is a string the client formatted in `buildLiveSnapshot()` —
 * Metabase auth lives on the desktop side, so the snapshot is pulled here
 * and shipped along the query rather than the Worker reaching for Metabase.
 */
export function askStream(
  query: string,
  history: AskTurn[],
  liveData: string,
  cb: AskCallbacks,
): AbortController {
  const ctrl = new AbortController();
  void run();
  return ctrl;

  async function run() {
    try {
      const res = await fetch(`${NOTES_ENDPOINT}/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, history, liveData }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        // Try to parse a structured error before falling back to status text.
        let detail = `${res.status} ${res.statusText}`;
        try {
          const text = await res.text();
          const parsed = JSON.parse(text) as { error?: string };
          if (parsed.error) detail = parsed.error;
          else detail = text.slice(0, 400);
        } catch {
          /* swallow */
        }
        cb.onError?.(detail);
        return;
      }

      // SSE parser. Anthropic's stream uses event lines + data lines,
      // double-newline-terminated. We accumulate raw bytes into a buffer
      // and split on blank lines to recover whole events.
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Split into complete SSE events.
        let sep: number;
        while ((sep = buf.indexOf("\n\n")) >= 0) {
          const raw = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          handleEvent(raw, cb);
        }
      }
      cb.onDone?.();
    } catch (e) {
      if ((e as Error)?.name === "AbortError") return;
      cb.onError?.(e instanceof Error ? e.message : String(e));
    }
  }
}

function handleEvent(raw: string, cb: AskCallbacks): void {
  // An SSE event looks like:
  //   event: name
  //   data: {...}
  // (multiple data: lines get joined with newlines; rarely matters here)
  let eventName = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  }
  if (dataLines.length === 0) return;
  const dataStr = dataLines.join("\n");

  // Our own pre-stream events from the Worker.
  if (eventName === "stage") {
    try {
      const parsed = JSON.parse(dataStr) as AskStage;
      if (parsed && typeof parsed === "object" && typeof parsed.phase === "string") {
        cb.onStage?.(parsed);
      }
    } catch (e) {
      console.warn("[ask] failed to parse stage", e);
    }
    return;
  }
  if (eventName === "citations") {
    try {
      const parsed = JSON.parse(dataStr) as { citations: Citation[] };
      cb.onCitations?.(parsed.citations ?? []);
    } catch (e) {
      console.warn("[ask] failed to parse citations", e);
    }
    return;
  }

  if (eventName === "error") {
    try {
      const parsed = JSON.parse(dataStr) as { error?: string };
      cb.onError?.(parsed.error ?? dataStr);
    } catch {
      cb.onError?.(dataStr);
    }
    return;
  }

  // Anthropic streaming events. We care about content_block_delta (text
  // tokens) and message_stop (clean finish). Everything else (ping,
  // message_start, content_block_start/stop, message_delta) is metadata
  // we can safely ignore for a minimal chat UI.
  if (eventName === "content_block_delta") {
    try {
      const parsed = JSON.parse(dataStr) as {
        delta?: { type?: string; text?: string };
      };
      if (parsed.delta?.type === "text_delta" && parsed.delta.text) {
        cb.onDelta?.(parsed.delta.text);
      }
    } catch {
      /* ignore malformed delta */
    }
    return;
  }
  // message_stop fires once at end; the outer loop will call onDone too,
  // so we don't double-dispatch here.
}
